"use client";

import { ArrowRight, Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const stickyNotes = [{
    label: "Data-driven OTA insights",
    top: "top-[12%]",
    left: "left-[6%]",
    rotate: "-rotate-3"
  }, {
    label: "Optimize tech stacks",
    top: "top-[38%]",
    left: "left-[58%]",
    rotate: "rotate-2"
  }, {
    label: "Maximize direct bookings",
    top: "top-[68%]",
    left: "left-[10%]",
    rotate: "-rotate-2"
  }];
  return <section data-section-id="513" id="herorevenueoptimization" className="relative bg-background text-foreground overflow-hidden min-h-[90vh] flex items-center">
      <div className="w-full max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-6 order-2 lg:order-1 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-4 mb-8">
              <span className="font-default text-xs tracking-[0.25em] uppercase text-secondary">
                — Hotel Revenue Experts
              </span>
              <span className="h-px w-16 bg-foreground/30" aria-hidden="true" />
            </div>

            {}
            <h1 className="font-default font-bold text-foreground leading-[1.02] text-[2.75rem] sm:text-5xl md:text-6xl lg:text-[5.25rem] tracking-tight mb-8">
              Maximize Your Hotel’s Revenue with{" "}
              <span className="italic font-normal text-secondary">Data-Driven</span>{" "}
              OTA Strategies.
            </h1>

            {}
            <p className="font-default text-lg leading-relaxed text-muted-foreground max-w-xl mb-10">
              Partner with HotelRevBaBa to unlock hidden revenue potential. We provide expert revenue management, hospitality tech implementation, and comprehensive audits to help your property thrive in a competitive market.
            </p>

            {}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-secondary text-primary-foreground hover:bg-secondary/90 font-default tracking-wide shadow-md h-12 px-7">
                <Link to="/ContactPage">
                  Book a Consultation
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="text-foreground hover:bg-accent hover:text-accent-foreground font-default tracking-wide h-12 px-7 border border-border/60">
                <Link to="#">
                  <Download className="mr-1 h-4 w-4" />
                  View Our Services
                </Link>
              </Button>
            </div>

            {}
            <div className="mt-12 flex items-center gap-6 font-default text-xs tracking-widest uppercase text-muted-foreground">
              <span>Sunil Singh</span>
              <span className="h-px w-8 bg-border" />
              <span>Revenue Expert</span>
              <span className="h-px w-8 bg-border" />
              <span>Est. 2024</span>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 order-1 lg:order-2 transition-all duration-1000 ease-out delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative">
              {}
              <div className="absolute -inset-4 sm:-inset-6 bg-card rounded-lg shadow-lg" style={{
              backgroundImage: "radial-gradient(circle at 20% 30%, hsl(222 47% 7% / 0.04) 1px, transparent 1px), radial-gradient(circle at 80% 70%, hsl(222 47% 7% / 0.03) 1px, transparent 1px)",
              backgroundSize: "24px 24px, 32px 32px"
            }} aria-hidden="true" />

              {}
              <div className="relative rounded-lg overflow-hidden shadow-xl border border-border/40">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_management_dashboard.png" alt="Professional hotel revenue management dashboard showing growth analytics and OTA performance metrics" loading="lazy" decoding="async" className="w-full h-auto aspect-[4/5] object-cover" />

                {}
                <div className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-30" style={{
                backgroundImage: "repeating-linear-gradient(45deg, transparent 0 2px, hsl(222 47% 7% / 0.02) 2px 4px)"
              }} aria-hidden="true" />
              </div>

              {}
              {stickyNotes.map((note, i) => <div data-index={i} key={i} className={`hidden sm:block absolute ${note.top} ${note.left} ${note.rotate} pointer-events-none`} style={{
              animationDelay: `${i * 150}ms`
            }}>
                  <div className={`relative bg-secondary/90 px-4 py-2.5 shadow-md max-w-[180px] transition-all duration-700 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: `${600 + i * 200}ms`,
                fontFamily: "var(--font-default)"
              }}>
                    <p className="text-sm leading-snug text-primary-foreground font-semibold">
                      {note.label}
                    </p>
                    {}
                    <svg className="absolute -bottom-8 -right-4 text-secondary" width="48" height="40" viewBox="0 0 48 40" fill="none" aria-hidden="true">
                      <path d="M2 2 Q 20 10, 30 25 M30 25 L 22 22 M30 25 L 28 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                    </svg>
                  </div>
                </div>)}

              {}
              <div className="sm:hidden mt-6 bg-secondary/90 px-4 py-3 -rotate-1 shadow-md inline-block">
                <p className="font-default text-sm font-semibold text-primary-foreground">
                  Data-driven OTA insights
                </p>
              </div>

              {}
              <div className="absolute -bottom-3 -right-2 sm:-bottom-4 sm:-right-4 bg-primary text-primary-foreground px-4 py-2 shadow-md rotate-2">
                <span className="font-default text-[10px] tracking-[0.2em] uppercase">
                  Revenue Growth
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}